import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowsLeftRight, TrendDown, TrendUp } from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import { IGapMes, useFinanceiroGaps } from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const fmtMes = (mes: string) => {
  const [y, m] = (mes || '').split('-');
  return y && m ? `${m}/${y}` : mes;
};

const GapRow = ({ g, max, index }: { g: IGapMes; max: number; index: number }) => {
  const recW = max > 0 ? Math.max(2, (g.a_receber / max) * 100) : 0;
  const pagW = max > 0 ? Math.max(2, (g.a_pagar / max) * 100) : 0;
  const aperto = g.gap < 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: 0.04 * index }}
    >
      <Box
        p={4}
        borderRadius="xl"
        bg={aperto ? 'rgba(220,38,38,0.04)' : 'transparent'}
        borderWidth="1px"
        borderColor={aperto ? 'rgba(220,38,38,0.15)' : 'rgba(15,23,42,0.06)'}
      >
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight={700} color="#0F172A">
            {fmtMes(g.mes)}
          </Text>
          <Flex align="center" gap={2}>
            <Text fontSize="xs" color="#94A3B8">
              gap
            </Text>
            <Text fontWeight={700} color={aperto ? '#DC2626' : '#16A34A'}>
              {brl(g.gap)}
            </Text>
          </Flex>
        </Flex>
        <Flex gap={2} align="center" mb={1.5}>
          <Text fontSize="10px" color="#16A34A" w="92px" textAlign="right" className="tabular-nums">
            {brl(g.a_receber)}
          </Text>
          <Box flex="1">
            <AnimatedBar pct={recW} cor="#16A34A" h="8px" delay={index * 0.05} />
          </Box>
        </Flex>
        <Flex gap={2} align="center">
          <Text fontSize="10px" color="#DC2626" w="92px" textAlign="right" className="tabular-nums">
            {brl(g.a_pagar)}
          </Text>
          <Box flex="1">
            <AnimatedBar pct={pagW} cor="#DC2626" h="8px" delay={index * 0.05 + 0.07} />
          </Box>
        </Flex>
      </Box>
    </motion.div>
  );
};

export const FinanceiroGapsPage = () => {
  const f = useFinanceiroFilters({ defaultPeriod: 'year' });
  const { data, isError } = useFinanceiroGaps({ base: 'vencimento', ...f.apiParams });

  const meses = data?.meses ?? [];
  const max = Math.max(1, ...meses.flatMap(m => [m.a_receber, m.a_pagar]));
  const gapTotal = (data?.total_receber ?? 0) - (data?.total_pagar ?? 0);

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Caixa & Previsão"
          titulo="Gaps — recebíveis × pagos"
          subtitulo="Por mês de vencimento: vermelho = vence mais a pagar do que a receber"
          icon={<ArrowsLeftRight size={24} weight="fill" />}
          cor="#F59E0B"
          info={
            <Text>
              Descasamento de caixa: para cada mês de vencimento, o que há <b>a receber</b> vs{' '}
              <b>a pagar</b> (em aberto). Gap negativo = aperto de caixa naquele mês.
            </Text>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar os gaps.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
          <KpiCard
            label="A receber (aberto)"
            valor={data?.total_receber ?? 0}
            color="#16A34A"
            icon={<TrendUp size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="A receber em aberto">
                Soma do <i>nao_pago</i> de RECEITA com vencimento no período. É o que ainda deve
                entrar.
              </InfoPopover>
            }
          />
          <KpiCard
            label="A pagar (aberto)"
            valor={data?.total_pagar ?? 0}
            color="#DC2626"
            icon={<TrendDown size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title="A pagar em aberto">
                Soma do <i>nao_pago</i> de DESPESA com vencimento no período.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Gap do período"
            valor={gapTotal}
            color={gapTotal >= 0 ? '#16A34A' : '#DC2626'}
            icon={<ArrowsLeftRight size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Gap">
                <b>A receber − A pagar</b> no período. Negativo = mais saída do que entrada prevista
                (atenção ao caixa).
              </InfoPopover>
            }
          />
        </SimpleGrid>

        <GlassCard intensity="strong" p={{ base: 3, md: 5 }}>
          {meses.length === 0 ? (
            <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
              Sem títulos em aberto no período.
            </Text>
          ) : (
            <Flex direction="column" gap={3}>
              {meses.map((g, i) => (
                <GapRow key={g.mes} g={g} max={max} index={i} />
              ))}
            </Flex>
          )}
        </GlassCard>
      </Box>
    </GlassPageBg>
  );
};
